import { useEffect } from "react";

/**
 * Injects application/ld+json into document head.
 * Data must match visible page content — never fabricate ratings/reviews/awards.
 */
export default function JsonLd({ id, data }) {
  useEffect(() => {
    if (!data) return undefined;
    const scriptId = id || "sokno-jsonld";
    let el = document.getElementById(scriptId);
    if (!el) {
      el = document.createElement("script");
      el.type = "application/ld+json";
      el.id = scriptId;
      document.head.appendChild(el);
    }
    el.textContent = JSON.stringify(data);
    return () => {
      const node = document.getElementById(scriptId);
      if (node) node.remove();
    };
  }, [id, data]);

  return null;
}

export function organizationSchema() {
  return {
    "@context": "https://schema.org",
    "@type": "Organization",
    name: "SoKno Productions",
    url: "https://soknoproductions.com/",
    logo: "https://soknoproductions.com/sokno-logo.png",
    description:
      "Dallas-based independent film production company developing original films and production partnerships.",
    address: {
      "@type": "PostalAddress",
      addressLocality: "Dallas",
      addressRegion: "TX",
      addressCountry: "US",
    },
    contactPoint: [
      {
        "@type": "ContactPoint",
        contactType: "general inquiries",
        email: "info@soknoproductions.com",
        areaServed: "US",
        availableLanguage: "English",
      },
    ],
  };
}

export function websiteSchema() {
  return {
    "@context": "https://schema.org",
    "@type": "WebSite",
    name: "SoKno Productions",
    url: "https://soknoproductions.com/",
    inLanguage: "en-US",
    publisher: {
      "@type": "Organization",
      name: "SoKno Productions",
    },
  };
}

export function breadcrumbSchema(items) {
  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: items.map((item, index) => ({
      "@type": "ListItem",
      position: index + 1,
      name: item.name,
      item: item.url,
    })),
  };
}

export function projectSchema(project, canonicalUrl) {
  if (!project) return null;
  const type = project.structuredData?.type || "Movie";
  const description =
    project.synopsis || project.logline || project.shortDescription || undefined;
  const data = {
    "@context": "https://schema.org",
    "@type": type,
    name: project.title,
    url: canonicalUrl,
    description,
    productionCompany: (project.productionCompanies || []).map((name) => ({
      "@type": "Organization",
      name,
    })),
  };
  if (project.genre?.length) data.genre = project.genre;
  if (project.runtimeMinutes) data.duration = `PT${project.runtimeMinutes}M`;
  if (project.productionYear) data.dateCreated = String(project.productionYear);
  if (project.releaseDate) data.datePublished = project.releaseDate;
  if (project.contentRating) data.contentRating = project.contentRating;
  if (project.poster || project.heroImage || project.ogImage) {
    data.image = project.poster || project.heroImage || project.ogImage;
  }
  // Never emit aggregateRating, review, or award fields unless approved data exists.
  if (project.awards?.length) {
    data.award = project.awards.map((a) => (typeof a === "string" ? a : a.name)).filter(Boolean);
  }
  return data;
}
